export interface MarketingCard {
	title: string;
	description: string;
}

export interface FaqItem {
	question: string;
	answer: string;
}

export interface RelatedLink {
	label: string;
	href: string;
	description: string;
}

export interface SourceLink {
	label: string;
	href: string;
}

export interface ComparisonRow {
	feature: string;
	clapio: string;
	competitor: string;
}

export interface ComparisonTable {
	title: string;
	description: string;
	competitorName: string;
	rows: ComparisonRow[];
	footnote?: string;
}

export interface SwitchStep {
	title: string;
	description: string;
}

export interface SwitchGuide {
	title: string;
	description: string;
	steps: SwitchStep[];
}

export interface PageContent {
	badge: string;
	heroLineOne: string;
	heroLineTwo: string;
	heroDescription: string;
	featureHeadingPrefix: string;
	featureHeadingAccent: string;
	featureDescription: string;
	seoHeadingPrefix: string;
	seoHeadingAccent: string;
	seoDescription: string;
	useCases: MarketingCard[];
	comparison?: ComparisonTable;
	sources?: SourceLink[];
	switchGuide?: SwitchGuide;
	faqs: FaqItem[];
	relatedLinks: RelatedLink[];
	downloadDescription: string;
}

const RELATED_PAGES: RelatedLink[] = [
	{
		label: "Home",
		href: "/",
		description:
			"Compare Clapio as an all-around screen recorder tool for demos, tutorials, and async updates.",
	},
	{
		label: "Screen Recorder for Mac",
		href: "/screen-recorder-for-mac/",
		description:
			"See the Mac-focused page for polished demos, tutorials, and walkthroughs on macOS.",
	},
	{
		label: "Screen Recorder for Windows",
		href: "/screen-recorder-for-windows/",
		description:
			"Explore the Windows-focused page for bug reports, onboarding videos, and team walkthroughs.",
	},
	{
		label: "Screen Recorder with Webcam",
		href: "/screen-recorder-with-webcam/",
		description:
			"View the webcam page for founder demos, course videos, and more personal async updates.",
	},
	{
		label: "Screen Recorder with Audio",
		href: "/screen-recorder-with-audio/",
		description: "Open the audio page for narrated walkthroughs, bug reports, and training videos.",
	},
	{
		label: "Camtasia Alternative",
		href: "/camtasia-alternative/",
		description:
			"Compare Clapio with Camtasia when you want polished screen recording without a heavier editor-first workflow.",
	},
	{
		label: "OBS Alternative",
		href: "/obs-alternative/",
		description:
			"Compare Clapio's built-in editing with OBS Studio's configurable recording and streaming workflow.",
	},
	{
		label: "Loom Alternative",
		href: "/loom-alternative/",
		description:
			"Compare Clapio with Loom when you want screen, webcam, audio, editing, and polished exports in one desktop workflow.",
	},
	{
		label: "QuickTime Player Alternative",
		href: "/quicktime-player-alternative/",
		description:
			"See why Mac users choose Clapio when they outgrow QuickTime Player for recording, editing, and export.",
	},
	{
		label: "Screen and Audio Recorder Windows",
		href: "/screen-and-audio-recorder-windows/",
		description:
			"Go deeper on Windows recording workflows that need both microphone and system audio capture.",
	},
	{
		label: "Cap Alternative",
		href: "/cap-alternative/",
		description:
			"Compare Clapio with Cap when you need native Linux support and fully on-device, offline AI captions and transcription.",
	},
	{
		label: "Screen Recorder for Linux",
		href: "/screen-recorder-for-linux/",
		description:
			"See the Linux-focused page for a cinematic, auto-zoom recorder that runs natively on Wayland and X11.",
	},
	{
		label: "Screen Studio for Windows",
		href: "/screen-studio-for-windows/",
		description:
			"Get Screen.studio-grade cinematic polish on Windows, where Screen.studio has no native app.",
	},
	{
		label: "Screen Studio for Linux",
		href: "/screen-studio-for-linux/",
		description:
			"Get Screen.studio-grade cinematic recording natively on Linux, including Wayland and X11.",
	},
	{
		label: "Screen Studio Alternative",
		href: "/screen-studio-alternative/",
		description:
			"Compare Clapio with Screen.studio when you want cinematic recording on Windows and Linux, not just macOS.",
	},
	{
		label: "How to Add Captions",
		href: "/blog/how-to-add-captions-to-a-screen-recording/",
		description:
			"Follow a private, on-device workflow to review captions and export burned-in subtitles, SRT, or VTT.",
	},
	{
		label: "How to Record a GIF from Your Screen",
		href: "/blog/how-to-record-a-gif-of-your-screen/",
		description:
			"Record or capture a short screen interaction, trim it, and export a compact animated GIF on Mac, Windows, or Linux.",
	},
	{
		label: "How to Record Your Screen on Linux",
		href: "/blog/how-to-record-screen-on-linux/",
		description:
			"Follow the practical Wayland and X11 guide for screen, system-audio, and webcam capture.",
	},
	{
		label: "Best Screen Recorder for Mac",
		href: "/blog/best-screen-recorder-for-mac-2026/",
		description:
			"Compare QuickTime Player, Clapio, and other Mac recording workflows before choosing a tool.",
	},
	{
		label: "Best Screen Recording Software",
		href: "/blog/best-screen-recording-software-2026/",
		description: "Compare screen recorders by platform, workflow, editing model, and export needs.",
	},
	{
		label: "Screen Studio vs Cap vs Clapio",
		href: "/blog/screen-studio-vs-cap-vs-clapio/",
		description:
			"Compare three cinematic recording workflows across platforms, captions, sharing, and price.",
	},
];

function selectRelatedPages(...hrefs: string[]): RelatedLink[] {
	return hrefs.map((href) => {
		const page = RELATED_PAGES.find((candidate) => candidate.href === href);
		if (!page) {
			throw new Error(`Unknown related page: ${href}`);
		}
		return page;
	});
}

const HOME_PAGE: PageContent = {
	badge: "On-device recording, editing & AI captions",
	heroLineOne: "A screen recorder tool",
	heroLineTwo: "worth sharing",
	heroDescription:
		"Create demos, tutorials, bug reports, and async updates in one place. Record screen, webcam, and audio, add zoom and cursor polish, generate captions with on-device transcription, and export polished MP4 videos and GIFs in minutes — all without your footage leaving your machine.",
	featureHeadingPrefix: "A screen recorder tool built to",
	featureHeadingAccent: "ship polished videos",
	featureDescription:
		"From screen capture and editing to on-device captions and export, Clapio helps teams record screen, webcam, and audio on Mac, Windows, and Linux in one streamlined, local-first workflow.",
	seoHeadingPrefix: "Why teams choose Clapio as their",
	seoHeadingAccent: "screen recorder tool",
	seoDescription:
		"Clapio is built for buyers who want polished output without a complex workflow — or a cloud dependency. It combines recording, quick editing, automatic on-device captions, and clean exports in one desktop app, so your footage never has to leave your computer.",
	useCases: [
		{
			title: "Product demos",
			description:
				"Record polished walkthroughs for launches, onboarding, and feature announcements without juggling a separate editing stack.",
		},
		{
			title: "Tutorials and explainers",
			description:
				"Highlight clicks, zoom into important steps, and keep every screen recording easy to follow for customers and teammates.",
		},
		{
			title: "Bug reports and async updates",
			description:
				"Capture screen, webcam, and audio together so engineering, support, and product teams can share clearer context faster.",
		},
		{
			title: "Captioned, accessible videos",
			description:
				"Generate accurate captions with on-device Whisper transcription, burn in subtitles, and export SRT or VTT files in 16 languages — without uploading a single frame.",
		},
	],
	faqs: [
		{
			question: "What makes Clapio a strong screen recorder tool?",
			answer:
				"Clapio combines screen recording, webcam capture, audio recording, quick editing, zoom effects, cursor polish, on-device captions, and export tools in one desktop app.",
		},
		{
			question: "Can Clapio add captions and transcribe my recordings?",
			answer:
				"Yes. Clapio transcribes your audio on-device using Whisper, generates automatic captions you can burn into the video, and exports subtitle files as SRT or VTT in 16 languages.",
		},
		{
			question: "Does my footage get uploaded to the cloud?",
			answer:
				"No. Clapio is local-first. Recording, editing, and AI transcription all run on your machine, so your footage never leaves your computer and the app keeps working offline — with no separate metered AI tier.",
		},
		{
			question: "Can I record my screen, webcam, and audio at the same time?",
			answer:
				"Yes. Clapio supports screen, webcam, microphone, and system audio recording so you can create tutorials, demos, and walkthroughs in one take.",
		},
		{
			question: "Does Clapio work on Mac, Windows, and Linux?",
			answer:
				"Yes. Clapio is available for macOS, Windows, and Linux, making it a practical cross-platform screen recorder for mixed teams.",
		},
		{
			question: "What can I export from Clapio?",
			answer:
				"You can export polished MP4 videos and GIFs for product marketing, bug reports, documentation, and social sharing.",
		},
	],
	relatedLinks: selectRelatedPages(
		"/screen-recorder-for-mac/",
		"/screen-recorder-for-windows/",
		"/screen-recorder-for-linux/",
		"/screen-recorder-with-webcam/",
		"/screen-recorder-with-audio/",
		"/blog/how-to-record-a-gif-of-your-screen/",
		"/blog/best-screen-recording-software-2026/",
	),
	downloadDescription: "Install the Clapio screen recorder tool on macOS, Windows, or Linux.",
};

const PAGES: Record<string, PageContent> = {
	"/": HOME_PAGE,
	"/screen-recorder-for-mac/": {
		badge: "Built for macOS workflows",
		heroLineOne: "A screen recorder for Mac",
		heroLineTwo: "that looks polished",
		heroDescription:
			"Record your Mac screen, webcam, and audio in one workflow, then clean it up with zoom, cursor polish, and fast exports that are ready to share with customers or teammates.",
		featureHeadingPrefix: "A Mac screen recorder built for",
		featureHeadingAccent: "polished demos",
		featureDescription:
			"Capture crisp Mac workflows, product walkthroughs, and tutorials with one recording and editing tool instead of a patchwork of utilities — and add on-device captions and transcription without sending anything to the cloud.",
		seoHeadingPrefix: "Why Clapio works as a",
		seoHeadingAccent: "screen recorder for Mac",
		seoDescription:
			"Mac buyers usually want clean output, fast editing, and fewer tools. Clapio gives you recording, polish, annotations, and export controls in one app.",
		useCases: [
			{
				title: "SaaS demos on macOS",
				description:
					"Show feature launches, onboarding flows, and polished browser walkthroughs from your Mac without handing footage off to another editor.",
			},
			{
				title: "Tutorial recording",
				description:
					"Record app tutorials with cursor spotlight, click effects, and zoom so each step stays clear for learners and customers.",
			},
			{
				title: "Client updates",
				description:
					"Share Mac screen recordings with webcam and voiceover for faster async reviews, feedback, and approvals.",
			},
		],
		comparison: {
			title: "Looking for a CamStudio alternative for Mac?",
			description:
				"CamStudio's current official system requirements list Windows 10 and Windows 11, with no native macOS download listed. Clapio is an option for Mac users who want screen, camera, and audio capture plus editing in one desktop workflow.",
			competitorName: "CamStudio",
			rows: [
				{
					feature: "Native platform",
					clapio: "macOS builds for Apple Silicon and Intel",
					competitor: "Windows 10 or Windows 11 (64-bit)",
				},
				{
					feature: "Capture workflow",
					clapio: "Record screen, webcam, microphone, and system audio together",
					competitor: "Record a monitor, active window, or selected region",
				},
				{
					feature: "After recording",
					clapio: "Trim footage and add zoom, cursor treatment, captions, and device frames",
					competitor: "Official documentation focuses on capture settings and saved video output",
				},
				{
					feature: "Export",
					clapio: "Export MP4 video or GIF",
					competitor: "Save MP4 using H.264, H.265, or AV1",
				},
			],
			footnote:
				"Facts checked August 30, 2026 against CamStudio's official FAQ and documentation. Product support can change, so confirm current requirements before downloading.",
		},
		sources: [
			{ label: "CamStudio system requirements and formats", href: "https://camstudio.org/faq" },
			{ label: "CamStudio V3 documentation", href: "https://camstudio.org/documentation" },
		],
		faqs: [
			{
				question: "Is Clapio a screen recorder for Mac only?",
				answer:
					"No. Clapio works on macOS, Windows, and Linux, but the Mac page focuses on workflows popular with macOS creators and product teams.",
			},
			{
				question: "Can I record Mac screen, webcam, and audio together?",
				answer:
					"Yes. Clapio supports screen, webcam, microphone, and system audio recording so you can produce full Mac walkthroughs in one session.",
			},
			{
				question: "Does Clapio include editing after recording on Mac?",
				answer:
					"Yes. You can trim takes, add zoom effects, smooth cursor motion, use device frames, and export polished MP4 videos and GIFs.",
			},
			{
				question: "Which Mac downloads are available?",
				answer:
					"Clapio offers macOS downloads for both Apple Silicon and Intel, so you can install it on current and older Mac hardware.",
			},
			{
				question: "Is CamStudio available for Mac?",
				answer:
					"CamStudio's official system requirements currently list Windows 10 and Windows 11, and its installation guide uses an EXE download. It does not list a native macOS version. Clapio provides native downloads for Apple Silicon and Intel Macs.",
			},
			{
				question: "How do I record my screen and camera at the same time on Mac?",
				answer:
					"In Clapio, select the screen or window you want to capture, enable the webcam and your microphone, add system audio if the demo needs it, then start recording. The camera overlay and screen footage stay in the same recording workflow for editing and export.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/quicktime-player-alternative/",
			"/screen-recorder-with-webcam/",
			"/screen-recorder-with-audio/",
			"/loom-alternative/",
			"/screen-studio-alternative/",
			"/blog/best-screen-recorder-for-mac-2026/",
			"/blog/how-to-record-a-gif-of-your-screen/",
		),
		downloadDescription:
			"Download Clapio for Apple Silicon or Intel Mac, with matching Windows and Linux builds available for the rest of your team.",
	},
	"/screen-recorder-for-windows/": {
		badge: "Made for Windows teams",
		heroLineOne: "A screen recorder for Windows",
		heroLineTwo: "built for real work",
		heroDescription:
			"Record Windows workflows with screen, webcam, microphone, and system audio, then turn rough captures into clear demos, bug reports, and onboarding videos without leaving the app.",
		featureHeadingPrefix: "A Windows screen recorder built for",
		featureHeadingAccent: "daily work",
		featureDescription:
			"Record full-screen or windowed workflows on Windows, then clean them up with zoom, cursor effects, on-device captions, and exports that are ready to send — all processed locally so footage never leaves the machine.",
		seoHeadingPrefix: "Clapio for a",
		seoHeadingAccent: "Windows recording workflow",
		seoDescription:
			"Windows buyers usually need screen and audio capture, webcam support, and quick exports. Clapio is built around that exact workflow.",
		useCases: [
			{
				title: "Bug reproduction videos",
				description:
					"Capture exact Windows issues with screen, voiceover, and optional webcam so support and engineering can move faster.",
			},
			{
				title: "Sales and onboarding demos",
				description:
					"Record polished product walkthroughs on Windows with a cleaner visual presentation than raw screen capture alone.",
			},
			{
				title: "Internal training",
				description:
					"Create repeatable process videos with callouts, zoom, and trimmed edits for operations, customer support, and enablement.",
			},
		],
		faqs: [
			{
				question: "Can I use Clapio as a screen recorder for Windows 11?",
				answer:
					"Yes. Clapio is designed for modern Windows workflows and gives you recording, editing, and export tools in one desktop app.",
			},
			{
				question: "Does Clapio record screen and audio on Windows?",
				answer:
					"Yes. You can record the screen, microphone, and system audio together, which is useful for product demos, walkthroughs, and issue reports.",
			},
			{
				question: "Can I add webcam overlay to Windows recordings?",
				answer:
					"Yes. Clapio supports webcam overlays so presenters can add context and a more personal walkthrough style.",
			},
			{
				question: "What export formats are available on Windows?",
				answer:
					"Clapio exports MP4 videos and GIFs, covering common needs for documentation, async updates, and social sharing.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-and-audio-recorder-windows/",
			"/screen-studio-for-windows/",
			"/screen-recorder-with-webcam/",
			"/screen-recorder-with-audio/",
			"/quicktime-player-alternative/",
			"/blog/how-to-record-a-gif-of-your-screen/",
		),
		downloadDescription:
			"Install Clapio on Windows for a polished recording workflow, with Mac and Linux downloads available for cross-platform teams.",
	},
	"/screen-recorder-with-webcam/": {
		badge: "Screen and webcam recording",
		heroLineOne: "Screen recorder with webcam",
		heroLineTwo: "for human walkthroughs",
		heroDescription:
			"Record your screen with webcam, microphone, and system audio so demos, tutorials, and async updates feel more personal, more credible, and easier to follow.",
		featureHeadingPrefix: "A screen recorder with webcam for",
		featureHeadingAccent: "demos and training",
		featureDescription:
			"Combine your face, voice, and product flow in one recording, then polish the result with zoom, cursor highlighting, trimming, and exports.",
		seoHeadingPrefix: "Why teams want a",
		seoHeadingAccent: "screen recorder with webcam",
		seoDescription:
			"Webcam overlays add trust and context. Clapio helps you combine that with polished screen capture and fast editing in one workflow.",
		useCases: [
			{
				title: "Founder demos",
				description:
					"Present product updates with your webcam on-screen so prospects and customers get a more human walkthrough.",
			},
			{
				title: "Course creation",
				description:
					"Record lessons with both screen and webcam, then add zoom and cursor emphasis to keep teaching videos engaging.",
			},
			{
				title: "Remote team updates",
				description:
					"Share decisions and project status asynchronously with a webcam-backed screen recording instead of another meeting.",
			},
		],
		faqs: [
			{
				question: "Can Clapio record screen and webcam at the same time?",
				answer:
					"Yes. Clapio records your screen and webcam together so you can create demos, tutorials, and updates with more personality and clarity.",
			},
			{
				question: "Can I include audio while recording with webcam?",
				answer:
					"Yes. You can record microphone and system audio alongside the webcam and screen capture in the same workflow.",
			},
			{
				question: "Is editing included after webcam recording?",
				answer:
					"Yes. Clapio includes trimming, zoom effects, cursor polish, annotations, and export tools after the recording is done.",
			},
			{
				question: "Who uses a screen recorder with webcam most often?",
				answer:
					"It is especially useful for educators, founders, sales teams, support teams, and anyone creating product walkthroughs or training content.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-recorder-for-mac/",
			"/screen-recorder-for-windows/",
			"/screen-recorder-with-audio/",
			"/loom-alternative/",
			"/blog/how-to-record-a-gif-of-your-screen/",
			"/blog/best-screen-recording-software-2026/",
		),
		downloadDescription:
			"Download Clapio to record your screen with webcam on macOS, Windows, or Linux from the same polished workspace.",
	},
	"/screen-recorder-with-audio/": {
		badge: "Screen and audio recording",
		heroLineOne: "Screen recorder with audio",
		heroLineTwo: "that stays clear",
		heroDescription:
			"Capture screen, microphone, and system audio together, then edit quickly so walkthroughs, bug reports, and training videos are clear the first time you send them.",
		featureHeadingPrefix: "A screen recorder with audio for",
		featureHeadingAccent: "clearer communication",
		featureDescription:
			"Capture your walkthrough once with screen, mic, and system sound, then refine it with annotations, zoom, and fast export settings — and turn the audio into accurate captions with on-device transcription, no upload required.",
		seoHeadingPrefix: "What to compare in a",
		seoHeadingAccent: "screen recorder with audio",
		seoDescription:
			"Audio quality can make or break a walkthrough. Clapio combines clear audio capture with polished screen recording and lightweight editing.",
		useCases: [
			{
				title: "Narrated walkthroughs",
				description:
					"Explain product flows with microphone narration and system audio in one take, then trim the result for a cleaner final cut.",
			},
			{
				title: "Bug reports with context",
				description:
					"Capture the issue on screen while describing what happened and preserving the exact audio cues from the app.",
			},
			{
				title: "Training and enablement",
				description:
					"Record internal process videos with voiceover so teammates can follow the why behind each step, not just the clicks.",
			},
		],
		faqs: [
			{
				question: "Can Clapio record screen and audio together?",
				answer:
					"Yes. Clapio records the screen along with microphone and system audio, which is useful for tutorials, demos, and technical walkthroughs.",
			},
			{
				question: "Does Clapio support both microphone and system sound?",
				answer:
					"Yes. You can capture your own narration and the app or desktop audio in the same recording workflow.",
			},
			{
				question: "Can I edit after recording audio?",
				answer:
					"Yes. You can trim takes, add zoom regions, polish cursor motion, annotate key moments, and export to MP4 or GIF.",
			},
			{
				question: "Is Clapio only for audio-heavy recordings?",
				answer:
					"No. Clapio is a general screen recorder tool, but this page highlights its usefulness when clear microphone and system audio capture matter most.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-and-audio-recorder-windows/",
			"/screen-recorder-with-webcam/",
			"/screen-recorder-for-mac/",
			"/screen-recorder-for-windows/",
			"/quicktime-player-alternative/",
			"/blog/how-to-add-captions-to-a-screen-recording/",
		),
		downloadDescription:
			"Download Clapio for a screen recorder with audio support on Mac, Windows, and Linux.",
	},
	"/camtasia-alternative/": {
		badge: "A lighter Camtasia alternative",
		heroLineOne: "A Camtasia alternative",
		heroLineTwo: "for a focused local workflow",
		heroDescription:
			"Compare Clapio's focused recording and editing workflow with Camtasia's broader multitrack editor, AI tools, templates, cursor controls, and production features.",
		featureHeadingPrefix: "A Camtasia alternative for",
		featureHeadingAccent: "focused screen communication",
		featureDescription:
			"Clapio helps teams record screen, webcam, and audio, then refine the result with zoom, cursor polish, annotations, and exports in one lighter desktop workflow.",
		seoHeadingPrefix: "Compare Clapio as a",
		seoHeadingAccent: "Camtasia alternative",
		seoDescription:
			"Camtasia provides a broad production editor. Clapio focuses on recording, local timeline edits, zoom, cursor treatment, captions, and MP4 or GIF export for shorter screen-based communication.",
		useCases: [
			{
				title: "Focused product demos",
				description:
					"Record the walkthrough, add the polish viewers need, and export without turning every demo into a full editing project.",
			},
			{
				title: "Tutorials with less overhead",
				description:
					"Use zoom, annotations, cursor emphasis, and cleaner exports without forcing the team into a heavier training-video workflow every time.",
			},
			{
				title: "Weekly async updates",
				description:
					"Use a focused record-edit-export workflow when the team creates recurring walkthroughs and does not need a broad production suite for every clip.",
			},
		],
		comparison: {
			title: "Clapio vs Camtasia at a glance",
			description:
				"Both tools record and edit screen-based video. Camtasia offers a broader multitrack production suite; Clapio keeps the workflow focused on local screen communication and export.",
			competitorName: "Camtasia",
			rows: [
				{
					feature: "Platforms",
					clapio: "macOS, Windows, and Linux",
					competitor: "macOS and Windows only",
				},
				{
					feature: "Workflow",
					clapio: "Record, auto-polish, and export in one pass",
					competitor: "Editor-first, project-based timeline",
				},
				{
					feature: "Auto-zoom on click",
					clapio: "Click-derived zoom regions plus manual timeline control",
					competitor: "AI auto zoom and pan plus manual animation controls",
				},
				{
					feature: "Cursor smoothing and click effects",
					clapio: "Built in, applied automatically",
					competitor: "Recorded mouse data, cursor path editing, scaling, and effects",
				},
				{
					feature: "Captions and transcription",
					clapio: "On-device Whisper, SRT/VTT export in 16 languages, works offline",
					competitor:
						"Audio transcription, generated captions, dynamic captions, and subtitle import",
				},
				{
					feature: "Editing model",
					clapio: "Focused timeline for screen recordings",
					competitor:
						"Broader multitrack editor with templates, assets, audio tools, and AI features",
				},
				{
					feature: "Price",
					clapio: "Free plan; Pro is $79 list / $59 founder price, one-time",
					competitor: "Essentials is listed at $179.88/year; other Camtasia plans vary",
				},
				{
					feature: "Best for",
					clapio: "Recurring product demos, tutorials, and async updates",
					competitor: "Long-form course production with heavy editing",
				},
			],
			footnote:
				"Facts checked August 30, 2026. Camtasia is the broader production suite; Clapio is the more focused local screen-recording workflow. Choose based on the editing depth, assets, collaboration, export, and billing model you need.",
		},
		sources: [
			{ label: "Camtasia features", href: "https://www.techsmith.com/camtasia/features/" },
			{ label: "Camtasia pricing", href: "https://www.techsmith.com/store/camtasia" },
		],
		switchGuide: {
			title: "How to switch from Camtasia to Clapio",
			description:
				"Test one representative recording before changing tools. This workflow shows what to compare.",
			steps: [
				{
					title: "Install Clapio on every platform your team uses",
					description:
						"Download Clapio for macOS, Windows, or Linux. There is no project format to migrate — your next recording simply starts in Clapio.",
				},
				{
					title: "Record screen, webcam, and audio in one take",
					description:
						"Pick the screen or window, enable microphone and system audio, and add a webcam overlay if you want a face on the walkthrough.",
				},
				{
					title: "Compare the zoom and cursor workflow",
					description:
						"Try Clapio's click-derived zoom, cursor treatment, backgrounds, and device frames against the Camtasia controls you use. Keep Camtasia if its deeper animation or production tools are required.",
				},
				{
					title: "Export MP4 or GIF, plus captions",
					description:
						"Ship MP4 up to 4K or a GIF for docs and PRs, and export SRT/VTT subtitles generated on-device when the video needs captions.",
				},
			],
		},
		faqs: [
			{
				question: "Why use Clapio instead of Camtasia?",
				answer:
					"Clapio is an alternative when a focused local record-edit-export workflow covers your demos, tutorials, and product communication. Camtasia remains a broader choice for multitrack production, assets, collaboration, and additional AI tools.",
			},
			{
				question: "Can Clapio still create polished tutorial videos?",
				answer:
					"Yes. Clapio supports screen, webcam, and audio capture plus zoom, cursor polish, annotations, framing, and polished exports for tutorial-style content.",
			},
			{
				question: "Is Clapio better for quick demos than Camtasia?",
				answer:
					"It depends on the workflow. Clapio is more focused on recurring screen-based demos; Camtasia provides broader editing and production capabilities. Test a representative project in both before switching.",
			},
			{
				question: "Does Clapio work across Mac, Windows, and Linux?",
				answer:
					"Yes. Clapio supports macOS, Windows, and Linux, which makes it more practical for mixed teams that need the same workflow everywhere.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/obs-alternative/",
			"/loom-alternative/",
			"/screen-studio-alternative/",
			"/cap-alternative/",
			"/blog/best-screen-recording-software-2026/",
			"/blog/how-to-add-captions-to-a-screen-recording/",
		),
		downloadDescription:
			"Download Clapio to compare its focused local workflow with Camtasia for your own screen-recording projects.",
	},
	"/obs-alternative/": {
		badge: "An OBS alternative for simpler workflows",
		heroLineOne: "An OBS alternative",
		heroLineTwo: "with editing built in",
		heroDescription:
			"Compare OBS Studio's configurable recording, scenes, sources, plugins, and livestreaming workflow with Clapio's focused capture, timeline editing, captions, and local export.",
		featureHeadingPrefix: "An OBS alternative built for",
		featureHeadingAccent: "less setup and less cleanup",
		featureDescription:
			"Clapio helps teams who do not want to manage scenes, sources, and extra post-production just to create a clear demo, tutorial, or bug report.",
		seoHeadingPrefix: "Compare OBS with a",
		seoHeadingAccent: "focused editing workflow",
		seoDescription:
			"OBS Studio is a free, open-source recorder and livestreaming tool with configurable scenes and sources. Clapio focuses on recorded screen communication with editing and captions in the same app.",
		useCases: [
			{
				title: "Cleaner demos with less setup",
				description:
					"Start recording quickly instead of preparing scenes and sources every time you need to explain a workflow.",
			},
			{
				title: "Polished bug reports and walkthroughs",
				description:
					"Capture the issue, highlight what matters, and export a clearer video without adding another editing tool to the process.",
			},
			{
				title: "Recurring async communication",
				description:
					"Use the same focused capture and editing workflow across product, support, and customer-success recordings.",
			},
		],
		comparison: {
			title: "Clapio vs OBS Studio at a glance",
			description:
				"OBS emphasizes configurable recording and live production. Clapio emphasizes local screen capture followed by timeline edits, captions, and file export in one app.",
			competitorName: "OBS Studio",
			rows: [
				{
					feature: "Setup before recording",
					clapio: "Pick a screen or window and hit record",
					competitor: "Scenes, sources, and profiles to configure",
				},
				{
					feature: "Built-in editing",
					clapio: "Timeline trim, zoom, annotations, backgrounds, device frames",
					competitor:
						"Recording and live-production focus; use a separate post-production workflow",
				},
				{
					feature: "Auto-zoom and cursor polish",
					clapio: "Built in",
					competitor: "Not a core OBS feature; plugins and scripts can extend OBS",
				},
				{
					feature: "Captions and transcription",
					clapio: "On-device Whisper, SRT/VTT export in 16 languages",
					competitor: "Extensible through community plugins and external caption workflows",
				},
				{
					feature: "Live streaming",
					clapio: "Not a focus — Clapio is built for recorded video",
					competitor: "Excellent — OBS's core strength",
				},
				{
					feature: "Price",
					clapio: "Free plan; Pro is $79 list / $59 founder price, one-time",
					competitor: "Free and open source",
				},
				{
					feature: "Primary workflow",
					clapio: "Record, edit, caption, and export MP4 or GIF",
					competitor: "Record or livestream a configured scene-and-source production",
				},
			],
			footnote:
				"Facts checked August 30, 2026. Choose OBS for configurable scenes, sources, plugins, and livestreaming. Consider Clapio when built-in post-recording edits, on-device captions, and file export match the job.",
		},
		sources: [{ label: "OBS Studio product and features", href: "https://obsproject.com/" }],
		switchGuide: {
			title: "How to switch from OBS to Clapio",
			description:
				"If you use OBS primarily for recorded demos rather than live production, test this workflow on one representative project.",
			steps: [
				{
					title: "Install Clapio",
					description:
						"Download Clapio for macOS, Windows, or Linux and confirm screen, microphone, system-audio, and webcam capture on the environment your team uses.",
				},
				{
					title: "Skip the scene setup",
					description:
						"Choose the screen, window, or area to record, toggle microphone, system audio, and webcam, and start recording — no scene collections needed.",
				},
				{
					title: "Polish instead of post-produce",
					description:
						"Trim dead space, add zoom to the clicks that matter, and let cursor smoothing and backgrounds make the video look intentional — inside the same app.",
				},
				{
					title: "Export and share",
					description:
						"Export MP4 up to 4K or GIF, plus on-device captions as SRT/VTT, and send the finished video instead of a raw capture.",
				},
			],
		},
		faqs: [
			{
				question: "Why choose Clapio over OBS Studio?",
				answer:
					"Consider Clapio when built-in timeline editing, zoom, cursor treatment, on-device captions, and MP4 or GIF export matter more than scenes, plugins, and livestreaming.",
			},
			{
				question: "Is OBS still better for streaming?",
				answer:
					"Yes. OBS remains strong for advanced streaming and scene-based setups. Clapio is aimed at teams that care more about recorded communication and final-video polish.",
			},
			{
				question: "Can Clapio record screen, webcam, and audio like OBS?",
				answer:
					"Yes. Clapio supports screen, webcam, microphone, and system audio capture, then lets you edit and export the result from the same workflow.",
			},
			{
				question: "Does Clapio help after the recording ends?",
				answer:
					"Yes. Clapio includes trimming, zoom, cursor treatment, annotations, captions, and export in the same desktop workflow.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/camtasia-alternative/",
			"/cap-alternative/",
			"/screen-recorder-for-linux/",
			"/screen-studio-alternative/",
			"/blog/how-to-record-screen-on-linux/",
			"/blog/best-screen-recording-software-2026/",
		),
		downloadDescription:
			"Download Clapio as an OBS alternative when you want a simpler way to create polished recordings for real work.",
	},
	"/loom-alternative/": {
		badge: "Free local-first Loom alternative",
		heroLineOne: "A free Loom alternative for Mac",
		heroLineTwo: "Windows & Linux",
		heroDescription:
			"Compare Loom's hosted sharing, workspaces, viewer interaction, analytics, and cloud transcription with Clapio's free local recording, editing, on-device captions, and 1080p file export.",
		featureHeadingPrefix: "A Loom alternative built for",
		featureHeadingAccent: "better-looking async video",
		featureDescription:
			"Clapio records and refines updates inside the same desktop app with local timeline editing, on-device transcription, caption burn-in, and MP4 or GIF export.",
		seoHeadingPrefix: "Compare Clapio with Loom as a",
		seoHeadingAccent: "Loom alternative",
		seoDescription:
			"Some buyers like Loom's speed but want a free Loom alternative for Mac, Windows, or Linux with more control over the final video and their data. Clapio adds editing, visual polish, automatic on-device captions, and export flexibility while keeping recordings local-first, so nothing has to upload to a cloud service.",
		useCases: [
			{
				title: "Polished async updates",
				description:
					"Record stakeholder updates, product walkthroughs, and customer follow-ups with the same speed as a quick recorder, then improve clarity before sending.",
			},
			{
				title: "Sales and onboarding demos",
				description:
					"Upgrade simple screen shares with zoom regions, cursor effects, framing, and exports that look more intentional during demos and onboarding.",
			},
			{
				title: "Bug reports that explain themselves",
				description:
					"Capture screen, webcam, and audio together, then trim and annotate the result so engineering and support teams can act faster.",
			},
			{
				title: "Captioned updates that stay private",
				description:
					"Auto-transcribe narration on-device, burn in subtitles, and export SRT or VTT in 16 languages — so async updates stay accessible without uploading footage to a third-party cloud.",
			},
		],
		comparison: {
			title: "Clapio vs Loom at a glance",
			description:
				"Loom is built around instant cloud links. Clapio is built around polished, private video you keep on your machine. Which one fits depends on where you want your footage to live.",
			competitorName: "Loom",
			rows: [
				{
					feature: "Desktop platforms",
					clapio: "macOS, Windows, and Linux",
					competitor: "macOS and Windows desktop apps, plus mobile apps",
				},
				{
					feature: "Where recordings live",
					clapio: "On your machine — local-first by design",
					competitor: "Uploaded to Loom's cloud",
				},
				{
					feature: "Workflow model",
					clapio: "Local recording, editing, on-device captions, and file export",
					competitor: "Hosted video workspace with links, comments, interaction, and analytics",
				},
				{
					feature: "Editing after recording",
					clapio: "Timeline editor with zoom, annotations, backgrounds, device frames",
					competitor: "Trim, stitch, transcript-based editing, and plan-dependent advanced editing",
				},
				{
					feature: "Auto-zoom and cursor effects",
					clapio: "Built in — cinematic polish from your clicks",
					competitor: "Not a focus",
				},
				{
					feature: "Captions and transcription",
					clapio: "On-device Whisper, SRT/VTT export in 16 languages",
					competitor: "Transcriptions and closed captions in Loom's hosted workflow",
				},
				{
					feature: "Instant share links",
					clapio: "Not built in — you share the exported file your way",
					competitor: "Yes — Loom's core strength",
				},
				{
					feature: "Free plan limits",
					clapio: "Unlimited recordings, 1080p export with a small Clapio outro",
					competitor: "25 videos, 5 minutes per screen recording, up to 720p",
				},
				{
					feature: "Public open-source code",
					clapio: "No public source repository; offered as Free and Pro desktop plans",
					competitor: "Hosted commercial video workspace",
				},
				{
					feature: "Export",
					clapio: "MP4 up to 4K and GIF",
					competitor: "MP4 download, plan-dependent",
				},
			],
			footnote:
				"Facts checked August 30, 2026. Choose Loom when hosted links, workspaces, viewer interaction, and analytics are central. Consider Clapio when local processing, on-device captions, and exported files fit the workflow.",
		},
		sources: [{ label: "Loom pricing and features", href: "https://www.loom.com/pricing" }],
		switchGuide: {
			title: "How to switch from Loom to Clapio",
			description:
				"Test how file-based sharing compares with Loom's hosted links before moving an established team workflow.",
			steps: [
				{
					title: "Install Clapio on your platforms",
					description:
						"Clapio provides desktop builds for macOS, Windows, and Linux. Confirm capture and export behavior on each platform your team uses.",
				},
				{
					title: "Test the capture controls",
					description:
						"Record screen, webcam, microphone, and system audio in one take, then compare the controls with the Loom workflow your team already knows.",
				},
				{
					title: "Apply local editing and captions",
					description:
						"Auto-zoom into clicks, smooth the cursor, trim dead air, and add a background or device frame before anyone sees the video.",
				},
				{
					title: "Share the file, keep the footage",
					description:
						"Export MP4 or GIF and share it in Slack, email, or your docs. Nothing is uploaded to a third-party cloud unless you choose to.",
				},
			],
		},
		faqs: [
			{
				question: "Is Clapio an open-source Loom alternative?",
				answer:
					"No. Clapio's current app source repository is not publicly accessible, so we do not present it as a public open-source Loom alternative. If public code or self-hosting is essential, compare Cap; if you need a free open-source recorder and livestreaming tool, compare OBS Studio. Choose Clapio for a free local-first recorder with built-in editing, on-device captions, and 1080p exports on Mac, Windows, and Linux.",
			},
			{
				question: "Why use Clapio as a Loom alternative?",
				answer:
					"Clapio is useful when local recording, zoom, cursor treatment, annotations, framing, on-device captions, and file export matter more than hosted links, workspace libraries, viewer interaction, or analytics.",
			},
			{
				question: "Does Clapio caption and transcribe like Loom, but privately?",
				answer:
					"Yes. Clapio transcribes audio on-device with Whisper, generates automatic captions you can burn into the video, and exports SRT or VTT subtitles in 16 languages — all without uploading your recording to the cloud.",
			},
			{
				question: "Where are my recordings stored?",
				answer:
					"On your machine. Unlike Loom's cloud-first model, Clapio is local-first: recording, editing, and transcription run on your computer, your footage never leaves it, and the app works offline.",
			},
			{
				question: "Can Clapio record screen, webcam, and audio like Loom?",
				answer:
					"Yes. Clapio supports screen recording, webcam overlay, microphone audio, and system audio in one workflow, then lets you edit before export.",
			},
			{
				question: "Does Clapio work on Mac, Windows, and Linux?",
				answer:
					"Yes. Clapio is available across macOS, Windows, and Linux, which makes it practical for mixed teams that need the same recording workflow everywhere.",
			},
			{
				question: "What can I export from Clapio?",
				answer: "Clapio exports MP4 videos and GIFs after you edit the project locally.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/camtasia-alternative/",
			"/screen-studio-alternative/",
			"/screen-recorder-with-webcam/",
			"/screen-recorder-with-audio/",
			"/screen-recorder-for-mac/",
			"/blog/best-screen-recording-software-2026/",
		),
		downloadDescription:
			"Download Clapio on Mac, Windows, or Linux to compare its local file-based workflow with Loom's hosted sharing model.",
	},
	"/quicktime-player-alternative/": {
		badge: "QuickTime Player alternative for Mac and Windows",
		heroLineOne: "A QuickTime Player alternative",
		heroLineTwo: "for Mac & Windows screen recording",
		heroDescription:
			"Clapio replaces QuickTime's screen-capture workflow, not its general media-player role. Record screen, webcam, microphone, and system audio on Mac or Windows, then edit, caption, and export the result.",
		featureHeadingPrefix: "A QuickTime recording alternative for",
		featureHeadingAccent: "editing, captions, and cross-platform capture",
		featureDescription:
			"Clapio combines capture and post-production for Mac and Windows users who need to turn a recording into a finished MP4 or GIF.",
		seoHeadingPrefix: "When to use Clapio as a",
		seoHeadingAccent: "QuickTime recording alternative",
		seoDescription:
			"QuickTime Player remains a convenient built-in Mac choice for simple capture and movie playback. Clapio is aimed at recorded tutorials, demos, and walkthroughs that need webcam, audio, editing, captions, or a Windows workflow.",
		useCases: [
			{
				title: "A QuickTime 7 alternative for Windows recording",
				description:
					"Use Clapio as a modern QuickTime equivalent for Windows screen recording: capture the screen, webcam, microphone, and system audio in one app. Apple states that QuickTime 7 for Windows is no longer supported.",
			},
			{
				title: "Mac demos with webcam and voiceover",
				description:
					"Create more personal walkthroughs with webcam overlay, microphone narration, and system audio in the same recording session.",
			},
			{
				title: "Polished exports without another editor",
				description:
					"Trim and refine recordings before export so your Mac demos, explainers, and onboarding videos look more intentional when shared.",
			},
		],
		comparison: {
			title: "Clapio vs QuickTime Player at a glance",
			description:
				"QuickTime Player provides built-in macOS screen capture and basic movie editing. Clapio adds a combined screen, webcam, audio, timeline-editing, caption, and export workflow.",
			competitorName: "QuickTime Player",
			rows: [
				{
					feature: "Price",
					clapio: "Free plan; Pro is $79 list / $59 founder price, one-time",
					competitor: "Free with macOS",
				},
				{
					feature: "System audio capture",
					clapio: "Built in",
					competitor:
						"Apple documents microphone selection; system-audio capture is not listed in the screen recorder",
				},
				{
					feature: "Webcam overlay on screen recordings",
					clapio: "Built in",
					competitor:
						"Apple documents screen recording and camera movie recording as separate workflows",
				},
				{
					feature: "Editing",
					clapio: "Timeline editor with zoom, annotations, cursor polish",
					competitor: "Trim, split, rearrange, rotate, and combine movie clips",
				},
				{
					feature: "Auto-zoom and cursor effects",
					clapio: "Built in",
					competitor:
						"Pointer and click capture options; no post-capture cursor effects documented",
				},
				{
					feature: "Captions and transcription",
					clapio: "On-device Whisper, SRT/VTT export in 16 languages",
					competitor:
						"No transcription or caption-generation workflow documented in the User Guide",
				},
				{
					feature: "Export",
					clapio: "MP4 up to 4K and GIF",
					competitor: "MOV files",
				},
				{
					feature: "Platforms",
					clapio: "macOS, Windows, and Linux",
					competitor: "macOS only",
				},
			],
			footnote:
				"Facts checked August 30, 2026 against Apple's QuickTime Player User Guide and QuickTime 7 for Windows support page. QuickTime is a capable built-in choice for simple Mac captures; Clapio covers Mac, Windows, and Linux recording workflows.",
		},
		sources: [
			{
				label: "Apple QuickTime Player User Guide",
				href: "https://support.apple.com/guide/quicktime-player/welcome-qtp1530a1918/mac",
			},
			{
				label: "Apple screen-recording guide",
				href: "https://support.apple.com/guide/quicktime-player/record-your-screen-qtp97b08e666/mac",
			},
			{
				label: "Apple QuickTime 7 for Windows support notice",
				href: "https://support.apple.com/en-ca/106375",
			},
		],
		switchGuide: {
			title: "How to move from QuickTime recording to Clapio",
			description:
				"Use this four-step workflow when your screen recording needs webcam, system audio, editing, captions, or Windows support.",
			steps: [
				{
					title: "Install Clapio for Mac",
					description:
						"Downloads are available for both Apple Silicon and Intel Macs, so it works across newer and older hardware.",
				},
				{
					title: "Test microphone and system-audio capture",
					description:
						"Capture microphone and system audio together in Clapio, then verify both tracks before recording an important session.",
				},
				{
					title: "Add a webcam overlay to the walkthrough",
					description:
						"Add a webcam overlay to the same screen recording rather than using QuickTime's separately documented screen and movie recording modes.",
				},
				{
					title: "Edit and export something shareable",
					description:
						"Trim, zoom, and polish the take, then export MP4 or GIF instead of a raw MOV — with on-device captions if you need them.",
				},
			],
		},
		faqs: [
			{
				question: "Why replace QuickTime screen recording with Clapio?",
				answer:
					"Clapio is useful when QuickTime's simple screen capture is no longer enough. It adds webcam support, richer audio capture, editing, zoom, annotations, and export tools in one app.",
			},
			{
				question: "Is Clapio a QuickTime 7 or media player replacement?",
				answer:
					"No. Clapio is a screen recorder and editor, not a general-purpose media player, legacy codec package, or drop-in replacement for QuickTime 7 Pro's complete editing workflow. Use it as an alternative to QuickTime's screen-recording workflow when you need webcam, audio, timeline editing, captions, MP4, or GIF export.",
			},
			{
				question: "What is a QuickTime equivalent for Windows screen recording?",
				answer:
					"Clapio runs on Windows and records screen, webcam, microphone, and system audio, then provides timeline editing and MP4 or GIF export. Apple states that QuickTime 7 for Windows is no longer supported.",
			},
			{
				question: "Can Clapio record webcam and audio on Mac?",
				answer:
					"Yes. Clapio supports screen recording with webcam overlay plus microphone and system audio, which helps Mac users create fuller demos and tutorials.",
			},
			{
				question: "Does Clapio let me edit after recording?",
				answer:
					"Yes. You can trim takes, add zoom effects, polish cursor movement, annotate key moments, and export polished MP4 videos or GIFs.",
			},
			{
				question: "Which Mac downloads are available?",
				answer:
					"Clapio offers downloads for both Apple Silicon and Intel Macs, so you can install it across newer and older macOS hardware.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-recorder-for-mac/",
			"/screen-recorder-for-windows/",
			"/screen-recorder-with-audio/",
			"/blog/best-screen-recorder-for-mac-2026/",
			"/blog/how-to-record-a-gif-of-your-screen/",
			"/screen-studio-alternative/",
		),
		downloadDescription:
			"Download Clapio as a QuickTime Player alternative for Mac, with Windows and Linux builds also available for broader teams.",
	},
	"/screen-and-audio-recorder-windows/": {
		badge: "Windows screen and audio capture",
		heroLineOne: "A screen and audio recorder",
		heroLineTwo: "for Windows teams",
		heroDescription:
			"Capture Windows screen, microphone, and system audio in one workflow, then clean it up with editing, zoom, cursor polish, and exports that are easier to share across your team.",
		featureHeadingPrefix: "A Windows screen and audio recorder for",
		featureHeadingAccent: "clearer communication",
		featureDescription:
			"Clapio helps Windows teams preserve both visuals and sound, then refine recordings before they turn into demos, walkthroughs, training, or bug reports.",
		seoHeadingPrefix: "What Windows teams need from a",
		seoHeadingAccent: "screen and audio recorder",
		seoDescription:
			"Many Windows buyers need both microphone and system audio, not just a screen capture. Clapio is built for that complete workflow, plus editing after the recording ends.",
		useCases: [
			{
				title: "Narrated software walkthroughs",
				description:
					"Record Windows product demos with your voice and the app's audio together so stakeholders can see and hear what matters in one pass.",
			},
			{
				title: "Issue reproduction with full context",
				description:
					"Capture the screen while preserving system sounds, alerts, or media playback, then explain the issue with microphone narration for faster debugging.",
			},
			{
				title: "Training and internal documentation",
				description:
					"Create Windows process videos with screen, system sound, and narration, then trim and annotate the final version before sharing with teammates.",
			},
		],
		faqs: [
			{
				question: "Can Clapio record screen and audio on Windows at the same time?",
				answer:
					"Yes. Clapio records the Windows screen along with microphone and system audio in the same workflow, which is helpful for demos, walkthroughs, and issue reports.",
			},
			{
				question: "Does Clapio support Windows 10 and 11?",
				answer:
					"Yes. Clapio is designed for modern Windows workflows and fits teams that need clearer recordings without a multi-tool editing pipeline.",
			},
			{
				question: "Can I edit Windows recordings after capture?",
				answer:
					"Yes. You can trim, add zoom regions, polish cursor motion, annotate important moments, and export to MP4 or GIF from the same app.",
			},
			{
				question: "Can I add webcam overlay too?",
				answer:
					"Yes. Clapio can also include webcam overlay for Windows recordings when you want a more human walkthrough style alongside screen and audio capture.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-recorder-for-windows/",
			"/screen-recorder-with-audio/",
			"/screen-recorder-with-webcam/",
			"/screen-studio-for-windows/",
			"/quicktime-player-alternative/",
			"/blog/how-to-add-captions-to-a-screen-recording/",
		),
		downloadDescription:
			"Download Clapio for Windows screen and audio recording, with matching Mac and Linux builds available for the rest of your team.",
	},
	"/cap-alternative/": {
		badge: "A local-first Cap alternative",
		heroLineOne: "A Cap alternative",
		heroLineTwo: "with on-device captions",
		heroDescription:
			"Choose Clapio when you want recording, editing, and Whisper transcription in one local-first desktop workflow. Automatic captions and caption burn-in run on your machine, while Cap is a strong option for instant links, collaboration, and self-hosting.",
		featureHeadingPrefix: "A Cap alternative built for",
		featureHeadingAccent: "private, on-device AI",
		featureDescription:
			"Clapio records screen, webcam, microphone, and system audio on macOS, Windows, and Linux, then generates captions locally. Cap now also publishes macOS, Windows, and Linux downloads and emphasizes cloud sharing, collaboration, and self-hosting.",
		seoHeadingPrefix: "Why teams pick Clapio as a",
		seoHeadingAccent: "Cap alternative",
		seoDescription:
			"The practical difference is workflow, not platform coverage. Clapio centers local projects, offline editing, and on-device Whisper captions. Cap centers fast share links and offers a local Studio mode, open-source code, self-hosting, and paid cloud AI and collaboration features.",
		useCases: [
			{
				title: "Local-first recording",
				description:
					"Record and edit projects locally on macOS, Windows, or Linux, then export the file yourself instead of making a share link the center of the workflow.",
			},
			{
				title: "Private, on-device captions",
				description:
					"Transcribe with on-device Whisper, burn in subtitles, and export SRT or VTT in 16 languages without uploading footage to a cloud AI service.",
			},
			{
				title: "Cinematic editing controls",
				description:
					"Use 6-level zoom, motion blur, device frames, annotations, and cursor controls before exporting the final video.",
			},
		],
		comparison: {
			title: "Clapio vs Cap at a glance",
			description:
				"Both tools record and edit locally and now publish Linux downloads. Clapio differentiates with on-device Whisper transcription and a one-time Pro license; Cap differentiates with instant links, team features, analytics, and self-hosting.",
			competitorName: "Cap",
			rows: [
				{
					feature: "Linux download",
					clapio: "AppImage; Wayland and X11 support",
					competitor: "x86_64 .deb for Debian-compatible distributions",
				},
				{
					feature: "AI captions and transcription",
					clapio: "On-device Whisper, included, works offline",
					competitor: "Transcriptions are listed with Cap Pro's AI features",
				},
				{
					feature: "Where footage lives",
					clapio: "Local project and export workflow",
					competitor: "Studio mode is local; Instant mode creates cloud share links",
				},
				{
					feature: "Editing focus",
					clapio: "6-level zoom, motion blur, device frames, cursor craft",
					competitor: "Studio mode with backgrounds, zoom effects, trimming, and 4K/60fps export",
				},
				{
					feature: "Instant cloud share links",
					clapio: "Not built in — local-first by design",
					competitor: "Yes — Cap's core strength",
				},
				{
					feature: "Open source",
					clapio: "No",
					competitor: "Yes",
				},
				{
					feature: "Platforms",
					clapio: "macOS, Windows, and Linux",
					competitor: "macOS, Windows, and Linux .deb",
				},
				{
					feature: "Subtitle export",
					clapio: "SRT and VTT in 16 languages, generated locally",
					competitor: "Transcription is included with Cap Pro",
				},
			],
			footnote:
				"Cap's open-source model, share links, collaboration, and self-hosting are meaningful strengths. Choose Clapio when on-device Whisper transcription, local exports, or a one-time Pro license fit better.",
		},
		sources: [
			{ label: "Cap downloads", href: "https://cap.so/download" },
			{ label: "Cap installation docs", href: "https://cap.so/docs/installation" },
			{ label: "Cap pricing", href: "https://cap.so/pricing" },
		],
		switchGuide: {
			title: "How to switch from Cap to Clapio",
			description: "Moving from Cap takes one recording session — here is what to expect.",
			steps: [
				{
					title: "Install Clapio for your desktop",
					description:
						"Grab the macOS, Windows, or Linux build. Clapio uses an AppImage on Linux; Cap currently publishes an x86_64 .deb package.",
				},
				{
					title: "Record the same way",
					description:
						"Screen, webcam, microphone, and system audio in one take — the capture workflow will feel immediately familiar.",
				},
				{
					title: "Turn on on-device captions",
					description:
						"Transcription runs locally with Whisper. Generate captions, burn them in, or export SRT/VTT in 16 languages without a cloud round-trip or a metered AI tier.",
				},
				{
					title: "Finish in the cinematic editor",
					description:
						"Add 6-level zoom, motion blur, device frames, and cursor polish, then export MP4 up to 4K or GIF.",
				},
			],
		},
		faqs: [
			{
				question: "Why use Clapio instead of Cap?",
				answer:
					"Clapio is a strong Cap alternative when you want on-device Whisper transcription, local-first exports, cinematic editing controls, and a one-time Pro license. Cap may fit better when instant share links, analytics, team workspaces, or self-hosting are priorities.",
			},
			{
				question: "Do both Clapio and Cap run on Linux?",
				answer:
					"Yes. Clapio publishes an AppImage and supports Wayland and X11. Cap's current download and installation pages publish an x86_64 .deb package for Debian-compatible distributions.",
			},
			{
				question: "Are Clapio's AI features on-device or cloud-based?",
				answer:
					"On-device. Clapio transcribes audio and generates captions locally with Whisper, so your recordings never leave your machine and you are not paying for or waiting on a cloud AI tier.",
			},
			{
				question: "When might Cap fit you better?",
				answer:
					"Cap can be a better fit if you want open-source code, instant share links, team workspaces, viewer analytics, or self-hosting. Clapio fits when you prefer on-device transcription, local exports, and a one-time Pro license.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-studio-alternative/",
			"/obs-alternative/",
			"/screen-recorder-for-linux/",
			"/screen-studio-for-linux/",
			"/blog/how-to-record-screen-on-linux/",
			"/blog/screen-studio-vs-cap-vs-clapio/",
		),
		downloadDescription:
			"Download Clapio as a Cap alternative on macOS, Windows, or Linux when you want on-device captions and a local-first export workflow.",
	},
	"/screen-recorder-for-linux/": {
		badge: "Local screen recording for Linux",
		heroLineOne: "A screen recorder for Linux",
		heroLineTwo: "with editing and captions",
		heroDescription:
			"Record a Linux desktop, webcam, microphone, and system audio, then edit locally with zoom, cursor treatment, annotations, on-device captions, and MP4 or GIF export on Wayland or X11.",
		featureHeadingPrefix: "A Linux screen recorder built for",
		featureHeadingAccent: "cinematic, shareable video",
		featureDescription:
			"Clapio brings auto-zoom, cursor polish, backgrounds, device frames, and on-device transcription to Linux, so you can go from capture to a clean MP4 or GIF without stitching together command-line tools.",
		seoHeadingPrefix: "Why Linux users choose Clapio as their",
		seoHeadingAccent: "screen recorder for Linux",
		seoDescription:
			"Clapio combines Linux capture, local timeline editing, on-device captions, and file export. Confirm display-server, audio, webcam, and export support against your own setup before recording an important session.",
		useCases: [
			{
				title: "Polished Linux demos",
				description:
					"Record product walkthroughs on Linux and use zoom, cursor treatment, backgrounds, and annotations to guide attention.",
			},
			{
				title: "Tutorials with captions",
				description:
					"Transcribe narration on-device, burn in subtitles, and export SRT or VTT in 16 languages so Linux tutorials stay accessible.",
			},
			{
				title: "Bug reports with full audio",
				description:
					"Capture screen, webcam, microphone, and system audio together on Wayland or X11 to give engineers complete context.",
			},
		],
		sources: [{ label: "OBS Studio product and features", href: "https://obsproject.com/" }],
		faqs: [
			{
				question: "Is Clapio a native screen recorder for Linux?",
				answer:
					"Yes. Clapio has a native Linux build that records screen, webcam, microphone, and system audio, with support for both Wayland and X11 sessions.",
			},
			{
				question: "Should I use Clapio or OBS Studio on Linux?",
				answer:
					"Choose OBS Studio when configurable scenes, sources, plugins, or livestreaming matter. Consider Clapio when your priority is a focused recorded-video workflow with built-in timeline edits, zoom, cursor treatment, on-device captions, and MP4 or GIF export.",
			},
			{
				question: "Can Clapio caption recordings on Linux without the cloud?",
				answer:
					"Yes. Clapio transcribes audio on-device with Whisper, generates captions you can burn in, and exports SRT or VTT subtitles in 16 languages — all locally, so your footage never leaves your machine.",
			},
			{
				question: "What can I export from Clapio on Linux?",
				answer:
					"Clapio exports polished MP4 videos and GIFs on Linux, the same formats available on macOS and Windows, so cross-platform teams stay consistent.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/blog/how-to-record-screen-on-linux/",
			"/screen-studio-for-linux/",
			"/obs-alternative/",
			"/cap-alternative/",
			"/blog/how-to-record-a-gif-of-your-screen/",
			"/blog/how-to-add-captions-to-a-screen-recording/",
		),
		downloadDescription:
			"Download Clapio for Linux to record cinematic screen videos on Wayland or X11, with matching Mac and Windows builds for your team.",
	},
	"/screen-studio-for-windows/": {
		badge: "Screen Studio is macOS-only",
		heroLineOne: "Screen Studio for Windows?",
		heroLineTwo: "Use Clapio on your PC",
		heroDescription:
			"Screen.studio does not offer a Windows download. Clapio runs natively on Windows 10 and 11, then adds automatic zoom, smooth cursor motion, backgrounds, device frames, on-device captions, and MP4/GIF export in one local workflow.",
		featureHeadingPrefix: "Screen Studio-grade recording built for",
		featureHeadingAccent: "Windows",
		featureDescription:
			"Clapio gives Windows users the cinematic auto-zoom, cursor craft, backgrounds, and device frames people love about Screen.studio, with recording, editing, and on-device captions in one local-first app.",
		seoHeadingPrefix: "A Screen Studio-style workflow",
		seoHeadingAccent: "for Windows",
		seoDescription:
			"Screen.studio's official download page currently offers macOS builds. Clapio provides a Windows option with auto-zoom, smooth cursor motion, backgrounds, device frames, and on-device captions.",
		useCases: [
			{
				title: "Cinematic product demos on PC",
				description:
					"Record Windows demos with auto-zoom and smooth cursor motion for the polished, Screen.studio-style look on hardware Screen.studio does not support.",
			},
			{
				title: "Marketing and launch videos",
				description:
					"Add backgrounds, device frames, and zoom emphasis to make Windows recordings look intentional for launches and social clips.",
			},
			{
				title: "Captioned walkthroughs",
				description:
					"Transcribe on-device, burn in subtitles, and export SRT or VTT in 16 languages without uploading footage anywhere.",
			},
		],
		comparison: {
			title: "Clapio vs Screen.studio on Windows",
			description:
				"The short version: Screen.studio does not run on Windows at all. Here is what Clapio gives you instead, feature by feature.",
			competitorName: "Screen.studio",
			rows: [
				{
					feature: "Native Windows app",
					clapio: "Yes — Windows 10 and 11",
					competitor: "No — macOS only",
				},
				{
					feature: "Auto-zoom on click",
					clapio: "Yes",
					competitor: "Yes, but only on macOS",
				},
				{
					feature: "Smooth cursor motion",
					clapio: "Yes",
					competitor: "Yes, but only on macOS",
				},
				{
					feature: "Backgrounds and device frames",
					clapio: "Yes",
					competitor: "Yes, but only on macOS",
				},
				{
					feature: "On-device captions and transcription",
					clapio: "Yes — Whisper, SRT/VTT in 16 languages, offline",
					competitor: "Not available to Windows users",
				},
				{
					feature: "Also covers your Mac and Linux teammates",
					clapio: "Yes — one workflow across all three platforms",
					competitor: "Mac only",
				},
				{
					feature: "Price",
					clapio: "Free plan; Pro is $79 list / $59 founder price, one-time",
					competitor: "Subscription with monthly and annual billing",
				},
			],
		},
		sources: [
			{ label: "Screen Studio product and pricing", href: "https://screen.studio/" },
			{ label: "Screen Studio download", href: "https://screen.studio/download" },
		],
		switchGuide: {
			title: "How to get the Screen.studio look on Windows",
			description:
				"You cannot install Screen.studio on a PC, but you can reproduce the style it is known for in four steps.",
			steps: [
				{
					title: "Install Clapio for Windows",
					description: "Download the native Windows build — no macOS device or VM required.",
				},
				{
					title: "Record your screen, webcam, and audio",
					description:
						"Capture the full screen or one window, with microphone and system audio, exactly like a Screen.studio session on a Mac.",
				},
				{
					title: "Apply the cinematic treatment",
					description:
						"Auto-zoom into clicks, smooth the cursor path, and add a background and device frame — the signature Screen.studio-style polish.",
				},
				{
					title: "Export in the format your channel needs",
					description:
						"MP4 up to 4K for launches and landing pages, GIF for docs and pull requests, plus on-device captions for accessibility.",
				},
			],
		},
		faqs: [
			{
				question: "Is there a Screen Studio for Windows?",
				answer:
					"Screen.studio's official download is for macOS, not Windows. Clapio offers automatic zoom, smooth cursor motion, backgrounds, and device frames in a native Windows app.",
			},
			{
				question: "Does Clapio match Screen.studio's cinematic look on Windows?",
				answer:
					"Clapio brings the core cinematic features people choose Screen.studio for — automatic zoom, smooth cursor motion, polished backgrounds, and device frames — to Windows, along with editing and export tools in one app.",
			},
			{
				question: "Can Clapio add captions on Windows?",
				answer:
					"Yes. Clapio transcribes audio on-device with Whisper, generates automatic captions you can burn into the video, and exports SRT or VTT subtitles in 16 languages — all without sending footage to the cloud.",
			},
			{
				question: "Does Clapio also work on Mac and Linux?",
				answer:
					"Yes. Clapio is cross-platform across macOS, Windows, and Linux, so mixed teams get the same cinematic recording workflow everywhere.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-studio-alternative/",
			"/screen-recorder-for-windows/",
			"/screen-and-audio-recorder-windows/",
			"/screen-studio-for-linux/",
			"/blog/how-to-record-a-gif-of-your-screen/",
			"/blog/best-screen-recording-software-2026/",
		),
		downloadDescription:
			"Download Clapio for Windows to get Screen.studio-grade cinematic recording, with Mac and Linux builds available too.",
	},
	"/screen-studio-for-linux/": {
		badge: "Screen Studio is macOS-only",
		heroLineOne: "Screen Studio for Linux?",
		heroLineTwo: "Use Clapio natively",
		heroDescription:
			"Screen.studio does not offer a Linux download. Clapio records natively on Wayland and X11, then adds automatic zoom, smooth cursor motion, backgrounds, device frames, on-device captions, and MP4/GIF export in one local workflow.",
		featureHeadingPrefix: "Screen Studio-grade recording built for",
		featureHeadingAccent: "Linux",
		featureDescription:
			"Clapio brings the cinematic auto-zoom, cursor craft, backgrounds, and device frames associated with Screen.studio to Linux, plus recording, editing, and on-device captions in one local-first app.",
		seoHeadingPrefix: "A Screen Studio-style workflow",
		seoHeadingAccent: "for Linux",
		seoDescription:
			"Screen.studio does not run on Linux, leaving Linux users without that cinematic style. Clapio is the native Linux answer: auto-zoom, smooth cursor motion, backgrounds, device frames, and on-device captions on both Wayland and X11.",
		useCases: [
			{
				title: "Screen Studio-style demos on Linux",
				description:
					"Record polished Linux walkthroughs with automatic zoom, smooth cursor motion, backgrounds, and device frames in one editing workflow.",
			},
			{
				title: "Wayland and X11 support",
				description:
					"Capture cleanly on modern Wayland sessions or classic X11 setups, then add backgrounds and device frames for a finished look.",
			},
			{
				title: "Private captioned video",
				description:
					"Transcribe on-device, burn in subtitles, and export SRT or VTT in 16 languages without uploading footage to any cloud service.",
			},
		],
		comparison: {
			title: "Clapio vs Screen.studio for Linux users",
			description:
				"Screen.studio's official download page offers Apple Silicon and Intel macOS builds. Clapio is a separate product that provides a comparable recording workflow on Wayland and X11.",
			competitorName: "Screen.studio",
			rows: [
				{
					feature: "Native Linux app",
					clapio: "Yes — Wayland and X11",
					competitor: "No — macOS only",
				},
				{
					feature: "Auto-zoom on click",
					clapio: "Yes",
					competitor: "Yes, but only on macOS",
				},
				{
					feature: "Smooth cursor motion",
					clapio: "Yes",
					competitor: "Yes, but only on macOS",
				},
				{
					feature: "Backgrounds and device frames",
					clapio: "Yes",
					competitor: "Yes, but only on macOS",
				},
				{
					feature: "On-device captions and transcription",
					clapio: "Yes — Whisper, SRT/VTT in 16 languages, offline",
					competitor: "Not available to Linux users",
				},
				{
					feature: "System audio and webcam capture on Linux",
					clapio: "Built in",
					competitor: "Not available",
				},
				{
					feature: "Price",
					clapio: "Free plan; Pro is $79 list / $59 founder price, one-time",
					competitor: "Subscription with monthly and annual billing",
				},
			],
		},
		sources: [
			{ label: "Screen Studio product and pricing", href: "https://screen.studio/" },
			{ label: "Screen Studio download", href: "https://screen.studio/download" },
			{
				label: "AppImage installation quickstart",
				href: "https://docs.appimage.org/introduction/quickstart.html",
			},
		],
		switchGuide: {
			title: "How to get the Screen.studio look on Linux",
			description:
				"No VM, no dual-boot, no waiting for a port. Four steps on your existing distro.",
			steps: [
				{
					title: "Download and run the AppImage",
					description:
						"Download Clapio's Linux AppImage, mark it as executable in your file manager or with chmod +x, and launch it. Clapio supports both Wayland and X11 sessions.",
				},
				{
					title: "Record the workflow",
					description:
						"Capture screen, webcam, microphone, and system audio together — the combinations Linux capture tools usually make you script.",
				},
				{
					title: "Apply cinematic polish",
					description:
						"Auto-zoom, smooth cursor motion, backgrounds, and device frames turn a raw capture into the style Screen.studio made famous.",
				},
				{
					title: "Export and caption locally",
					description:
						"Export MP4 up to 4K or GIF, and generate captions on-device with SRT/VTT export in 16 languages — nothing leaves your machine.",
				},
			],
		},
		faqs: [
			{
				question: "Is there a Screen Studio for Linux?",
				answer:
					"Screen.studio's official download page currently offers Apple Silicon and Intel macOS builds, not Linux. Clapio is an independent alternative with a native Linux workflow for Wayland and X11.",
			},
			{
				question: "Is Clapio an official Screen Studio port?",
				answer:
					"No. Clapio is an independent screen recorder and editor. It is presented as an alternative because it offers a similar cinematic workflow on Linux; it is not made by or affiliated with Screen Studio.",
			},
			{
				question: "How do I install Clapio on Linux?",
				answer:
					"Download the Clapio AppImage, make the file executable in your file manager or run chmod +x on it, then open the AppImage. The official AppImage quickstart documents both methods.",
			},
			{
				question: "Does Clapio support Wayland and X11?",
				answer:
					"Yes. Clapio runs natively on Linux with support for both Wayland and X11 sessions, so you get cinematic recording regardless of your display server.",
			},
			{
				question: "Does Clapio bring Screen.studio's cinematic style to Linux?",
				answer:
					"Yes. Clapio brings automatic zoom, smooth cursor motion, polished backgrounds, and device frames — the cinematic features people choose Screen.studio for — natively to Linux, with editing and export in the same app.",
			},
			{
				question: "Can Clapio caption recordings on Linux without the cloud?",
				answer:
					"Yes. Clapio transcribes audio on-device with Whisper, generates captions you can burn in, and exports SRT or VTT subtitles in 16 languages — all locally, so your footage never leaves your machine.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-studio-alternative/",
			"/screen-recorder-for-linux/",
			"/blog/how-to-record-screen-on-linux/",
			"/obs-alternative/",
			"/cap-alternative/",
			"/blog/how-to-record-a-gif-of-your-screen/",
		),
		downloadDescription:
			"Download Clapio for Linux to get Screen.studio-grade cinematic recording on Wayland or X11, with Mac and Windows builds available too.",
	},
	"/screen-studio-alternative/": {
		badge: "A real free plan with video export",
		heroLineOne: "A free Screen Studio alternative",
		heroLineTwo: "for Windows, Linux & Mac",
		heroDescription:
			"Start free with unlimited local recording, editing, and 1080p MP4/GIF export on Windows, Linux, or Mac. Upgrade once for auto-zoom, smooth cursor motion, device frames, on-device captions, and no export outro.",
		featureHeadingPrefix: "A Screen Studio alternative built for",
		featureHeadingAccent: "cross-platform teams",
		featureDescription:
			"Clapio covers the full cinematic workflow — record screen, webcam, and audio, auto-polish the footage, caption it on-device, and export MP4 or GIF — on macOS, Windows, and Linux.",
		seoHeadingPrefix: "What to compare in a",
		seoHeadingAccent: "Screen Studio alternative",
		seoDescription:
			"The clearest differences are platform coverage and licensing. Screen.studio's official app is for macOS and includes on-device transcription. Clapio runs on macOS, Windows, and Linux, keeps transcription on-device, and offers a free tier plus a one-time Pro license.",
		useCases: [
			{
				title: "Mixed-platform product teams",
				description:
					"Give designers on Macs, engineers on Linux, and everyone on Windows the same cinematic recording workflow instead of a Mac-only tool.",
			},
			{
				title: "Cinematic demos without a Mac",
				description:
					"Produce the auto-zoom, smooth-cursor launch videos Screen.studio is known for, natively on Windows 10/11 or any major Linux distro.",
			},
			{
				title: "Private, captioned marketing video",
				description:
					"Generate captions with on-device Whisper and export SRT or VTT in 16 languages — no cloud transcription step after the cinematic edit.",
			},
		],
		comparison: {
			title: "Clapio vs Screen.studio at a glance",
			description:
				"Both tools create polished screen recordings and both can transcribe locally. The main differences are supported operating systems, specific editing controls, subtitle export, and licensing.",
			competitorName: "Screen.studio",
			rows: [
				{
					feature: "Free use and video export",
					clapio: "Free plan exports 1080p MP4/GIF with a small Clapio outro",
					competitor: "All features usable without a plan, except video-file export",
				},
				{
					feature: "macOS app",
					clapio: "Yes — Apple Silicon and Intel",
					competitor: "Yes",
				},
				{
					feature: "Windows app",
					clapio: "Yes — Windows 10 and 11",
					competitor: "No",
				},
				{
					feature: "Linux app",
					clapio: "Yes — Wayland and X11",
					competitor: "No",
				},
				{
					feature: "Auto-zoom on click",
					clapio: "Yes",
					competitor: "Yes",
				},
				{
					feature: "Smooth cursor motion and click effects",
					clapio: "Yes",
					competitor: "Yes",
				},
				{
					feature: "Backgrounds and device frames",
					clapio: "Yes",
					competitor: "Yes",
				},
				{
					feature: "Captions and transcription",
					clapio: "On-device Whisper, SRT/VTT in 16 languages, offline",
					competitor: "On-device transcript and subtitles; see current product page",
				},
				{
					feature: "Webcam and system audio capture",
					clapio: "Built in on all platforms",
					competitor: "Built in, macOS only",
				},
				{
					feature: "Price",
					clapio: "Free plan; Pro is $79 list / $59 founder price, one-time",
					competitor: "Subscription with monthly and annual billing",
				},
			],
			footnote:
				"If your whole team is on Macs, Screen.studio offers strong automatic editing, local transcription, and share links. Clapio is the alternative when Windows, Linux, SRT/VTT export, or a one-time license matters.",
		},
		sources: [
			{ label: "Screen Studio product and pricing", href: "https://screen.studio/" },
			{ label: "Screen Studio download", href: "https://screen.studio/download" },
		],
		switchGuide: {
			title: "How to switch from Screen.studio to Clapio",
			description:
				"The mental model transfers directly — record, let the tool add the cinema, export. Four steps.",
			steps: [
				{
					title: "Install Clapio on every platform you actually use",
					description:
						"macOS, Windows, and Linux builds are all first-class. One purchase covers the workflow everywhere.",
				},
				{
					title: "Record as usual",
					description:
						"Screen, window, or area — with webcam overlay, microphone, and system audio in the same take.",
				},
				{
					title: "Keep your signature style",
					description:
						"Auto-zoom regions generated from clicks, smooth cursor paths, motion blur, backgrounds, and device frames replicate the look your videos already have.",
				},
				{
					title: "Export captions for your workflow",
					description:
						"Transcribe on-device, burn in captions, and export SRT or VTT in 16 languages alongside your MP4 or GIF. Screen.studio also advertises on-device transcript generation and subtitles.",
				},
			],
		},
		faqs: [
			{
				question: "Is there a free Screen Studio alternative?",
				answer:
					"Yes. Clapio's free plan includes unlimited local recordings, timeline editing, and 1080p MP4/GIF export with a small Clapio outro on Windows, Linux, and macOS. Screen Studio says users without an active plan can use its features except exporting video files.",
			},
			{
				question: "What is the best Screen Studio alternative?",
				answer:
					"Clapio is a close alternative when you want automatic zoom, smooth cursor motion, backgrounds, and device frames on Windows or Linux as well as macOS. Compare export controls, caption formats, and pricing before choosing.",
			},
			{
				question: "Is there a Screen Studio version for Windows or Linux?",
				answer:
					"Screen.studio's official download page currently offers Apple Silicon and Intel macOS builds. Clapio ships native builds for Windows 10/11 and Linux (Wayland and X11), as well as macOS.",
			},
			{
				question: "Does Clapio match Screen.studio's editing features?",
				answer:
					"Clapio covers the signature features — automatic zoom on clicks, smooth cursor motion, click effects, backgrounds, and device frames — and adds a timeline editor, annotations, and on-device Whisper captions with SRT/VTT export in 16 languages.",
			},
			{
				question: "How does Clapio's pricing compare?",
				answer:
					"Clapio's free plan includes local recording, editing, and 1080p exports with a small outro. Clapio Pro is a one-time $79 list-price license, currently shown at a $59 founder price. Screen.studio currently uses monthly or yearly subscription billing.",
			},
			{
				question: "Can I try Clapio before paying?",
				answer:
					"Yes — Clapio has a real free plan, not a trial: unlimited local recordings, the timeline editor, and 1080p MP4/GIF export with a small Clapio outro. Pro is a one-time purchase that unlocks everything, including all on-device AI.",
			},
		],
		relatedLinks: selectRelatedPages(
			"/screen-studio-for-windows/",
			"/screen-studio-for-linux/",
			"/cap-alternative/",
			"/loom-alternative/",
			"/camtasia-alternative/",
			"/blog/screen-studio-vs-cap-vs-clapio/",
		),
		downloadDescription:
			"Download Clapio as your Screen Studio alternative on macOS, Windows, or Linux — same cinematic style, every platform.",
	},
};

export function getPageContent(pathname: string): PageContent {
	if (!pathname || pathname === "/index.html") {
		return HOME_PAGE;
	}

	const normalizedPath = pathname.endsWith("/") ? pathname : `${pathname}/`;

	return PAGES[normalizedPath] ?? HOME_PAGE;
}
